import {
  ApolloClient,
  NormalizedCacheObject,
  QueryOptions,
  OperationVariables,
} from "@apollo/client";
import { AgGrid } from "@jsq/ds/core";
import { client } from "~/apollo/client";
import { GetOverviewDataDocument } from "~/graphql/supergraphHooks";
import { type GetOverviewDataQuery } from "~/graphql/supergraphTypes";
import {
  OverviewFilters,
  OverviewSort,
  RowItem,
  TextFilter,
  DateFilter,
  OverviewDataTypeRows,
} from "~/pages/Billing/ClientOverview/types";

export const getFilterVars = (
  filterModel: AgGrid.FilterModel | AgGrid.AdvancedFilterModel | null,
): OverviewFilters[] | null => {
  if (!filterModel) {
    return null;
  }
  if (filterModel?.filterType) {
    throw Error("Handling for AgGrid.AdvancedFilterModel not implemented.");
  }
  const queryFilters: OverviewFilters[] = [];
  Object.entries(filterModel).forEach((filter) => {
    const [field, filterInfo] = filter;
    // for now, we're not allowing multiple filters for field, so we won't hit this case
    if (filterInfo.conditions) {
      if (filterInfo.operator == "OR") {
        queryFilters.push({
          field: field,
          filterValues: filterInfo.conditions.map((condition: TextFilter) => condition.filter),
        });
      } else if (filterInfo.operator == "AND") {
        filterInfo.conditions.forEach((condition: TextFilter) => {
          queryFilters.push({ field: field, filterValues: [condition.filter] });
        });
      } else {
        // Shouldn't hit this error
        throw Error("Unexpected operator for multiple conditions");
      }
    } else if (filterInfo.filterType == "set") {
      queryFilters.push({ field: field, filterValues: filterInfo.values });
    } else if (filterInfo.filterType == "text") {
      queryFilters.push({ field: field, filterValues: [filterInfo.filter] });
    } else if (filterInfo.filterType == "number") {
      queryFilters.push({ field: field, filterValues: [filterInfo.filter.toString()] });
    } else if (filterInfo.filterType == "date") {
      // Handle date filters (agDateColumnFilter)
      const dateFilter = filterInfo as DateFilter;

      if (dateFilter.type === "equals") {
        queryFilters.push({
          field: field,
          filterValues: [dateFilter.dateFrom],
        });
      } else {
        throw Error("Only equals filter type is supported for date filters");
      }
    } else {
      // Would need to change this if we add other filter types
      throw Error("Unsupported filter type");
    }
  });
  return queryFilters;
};

export const getSortVars = (sortItems: AgGrid.SortModelItem[]): OverviewSort[] | null => {
  if (!sortItems) {
    return null;
  }
  const querySorts: OverviewSort[] = [];
  for (const item of sortItems) {
    querySorts.push({
      field: item.colId,
      sortAscending: item.sort === "asc",
    });
  }
  return querySorts.length > 0 ? querySorts : [];
};

export const rowsFromQuery = (OverviewData: OverviewDataTypeRows | null | undefined) => {
  if (!OverviewData) {
    return [];
  }
  const rowData: RowItem[] =
    OverviewData.map((clientdata) => {
      return {
        client: clientdata.client.toString(),
        clientId: clientdata.clientId,
        arenaId: clientdata.arenaId,
        csm: clientdata.csm,
        nextRenewalDate: clientdata.nextRenewalDate,
        productLine: clientdata.productLine,
        edition: clientdata.edition,
        currentBlendSoftwareEum: clientdata.currentBlendSoftwareEum,
        faEumUsd: clientdata.faEumUsd,
      };
    }) ?? [];
  return rowData;
};

export interface OverviewGraphqlDataSourceProps {
  onFiltersChange?: (filters: OverviewFilters[] | null) => void;
  onSortsChange?: (sorts: OverviewSort[] | null) => void;
  initialFilters?: OverviewFilters[] | null;
  initialSorts?: OverviewSort[] | null;
  initialPage?: number;
}

export class OverviewGraphqlDataSource implements AgGrid.IServerSideDatasource {
  client: ApolloClient<NormalizedCacheObject>;
  asOfDate: string;
  private onFiltersChange?: (filters: OverviewFilters[] | null) => void;
  private onSortsChange?: (sorts: OverviewSort[] | null) => void;

  constructor(
    props?: OverviewGraphqlDataSourceProps,
    apolloClient: ApolloClient<NormalizedCacheObject> = client,
    asOfDate: string = "",
  ) {
    this.client = apolloClient;
    this.asOfDate = asOfDate;
    this.onFiltersChange = props?.onFiltersChange;
    this.onSortsChange = props?.onSortsChange;
  }

  setAsOfDate(asOfDate: string): void {
    this.asOfDate = asOfDate;
  }

  public handleError(params: AgGrid.IServerSideGetRowsParams): void {
    params.fail();
  }

  public handleSuccess(
    params: AgGrid.IServerSideGetRowsParams,
    response: GetOverviewDataQuery,
  ): void {
    params.success({
      rowData: rowsFromQuery(response.getGpxData.rows),
      rowCount: response.getGpxData.filteredCount,
    });
  }

  public buildQueryOptions(
    params: AgGrid.IServerSideGetRowsParams,
  ): QueryOptions<OperationVariables, GetOverviewDataQuery> {
    const filters = getFilterVars(params.request.filterModel);
    const sorts = getSortVars(params.request.sortModel);
    const offset = params.request.startRow ?? 0;
    const limit = (params.request.endRow ?? 0) - (params.request.startRow ?? 0);

    if (this.onFiltersChange) {
      this.onFiltersChange(filters);
    }

    if (this.onSortsChange) {
      this.onSortsChange(sorts);
    }

    return {
      query: GetOverviewDataDocument,
      variables: {
        asOfDate: this.asOfDate,
        offset: offset,
        limit: limit,
        filters: filters,
        sorts: sorts,
      },

      fetchPolicy: "no-cache",
    };
  }

  public executeQuery(
    params: AgGrid.IServerSideGetRowsParams,
    queryOptions: ReturnType<typeof this.buildQueryOptions>,
  ): Promise<void> {
    return this.client
      .query<GetOverviewDataQuery>(queryOptions)
      .then((response) => {
        this.handleSuccess(params, response.data);
      })
      .catch((_error) => {
        this.handleError(params);
      });
  }

  public getRows(params: AgGrid.IServerSideGetRowsParams): void {
    const queryOptions = this.buildQueryOptions(params);
    void this.executeQuery(params, queryOptions);
  }
}
